import React, { useEffect, useMemo, useState } from "react";
import {
	CCButton,
	CCCombobox,
	CCDateField,
	CCField,
	CCModal,
	CCSelect,
	CCSwitch,
} from "@pymblecitizencoders/cc-ui";
import { toDatePart, toIso, toTimePart } from "../../utils/eventDates";
import { EVENT_CATEGORIES } from "../../types/event";
import {
	CUSTOM_UNITS,
	CustomUnit,
	FREQUENCY_OPTIONS,
	MAX_OCCURRENCES,
	RecurrenceFrequency,
	RecurrenceRule,
	WEEKDAY_CHIPS,
	expandRecurrence,
	weekdayOf,
} from "../../utils/eventRecurrence";
import { addMinutes } from "../../utils/eventRequest";
import { NewEventPayload } from "../../hooks/useEventManager";
import { useEventTypes } from "../../hooks/useEventTypes";
import { useConcurrentEvents } from "../../hooks/useConcurrentEvents";
import { EventClashPanel } from "./EventClashPanel";
import "./eventManager.css";

interface EventCreateDialogProps {
	open: boolean;
	/**
	 * The instant the person started from, local-naive.
	 *
	 * A midnight time means a whole-day cell (a month or week square, or the
	 * Add event button), so the dialog opens as all-day. Any other time came
	 * from a schedule slot, so it opens timed at that hour.
	 */
	initialDate: string;
	/**
	 * Where a drag finished, local-naive. Absent for a click.
	 *
	 * Dragging across the grid states a duration, so it is used verbatim rather
	 * than assuming the default hour off the start.
	 */
	initialEnd?: string;
	saving: boolean;
	onClose: () => void;
	onCreate: (payload: NewEventPayload) => Promise<void>;
}

/** `YYYY-MM-DD` as "1 Sep 2026", read as a plain date so no timezone can shift it. */
const longDay = (day: string): string => {
	const [y, m, d] = day.split("-").map(Number);
	return new Date(y, m - 1, d).toLocaleDateString("en-AU", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
};

/** Every event made here goes on the first calendar; the picker is gone. */
const NEW_EVENT_CATEGORY = EVENT_CATEGORIES[0].id;

/** A whole day, matching what double-clicking an empty cell already creates. */
const ALL_DAY_START = "00:00";
const ALL_DAY_END = "23:59";
/** A timed event defaults to an hour. */
const DEFAULT_DURATION_MINUTES = 60;


/** Date plus an optional time, the pairing the sidebar already uses. */
export const DateTimeRow: React.FC<{
	label: string;
	date: string;
	time: string;
	showTime: boolean;
	disabled: boolean;
	min?: string;
	onDate: (value: string) => void;
	onTime: (value: string) => void;
}> = ({ label, date, time, showTime, disabled, min, onDate, onTime }) => (
	<>
		<CCDateField
			label={label}
			fullWidth
			/* CCDateField brands its value as IsoDate. The app carries plain
			   `YYYY-MM-DD` strings, so the cast is confined to this boundary
			   rather than spreading the branded type through the form state. */
			value={date as never}
			min={min as never}
			disabled={disabled}
			onChange={(value) => onDate(String(value))}
		/>
		{showTime && (
			<CCField
				label={`${label} time`}
				type="time"
				size="sm"
				fullWidth
				className="em-mt-sm"
				value={time}
				disabled={disabled}
				onChange={(e) => onTime(e.target.value)}
			/>
		)}
	</>
);

/**
 * Quick-create dialog for a new event.
 *
 * Deliberately NOT the whole event. It asks for the things that cannot be
 * guessed — what it is called, when it runs, which calendar it belongs to — and
 * leaves groups, permissions, notices and the banner to the editor, which is
 * where the app already puts them and where they have room to be done properly.
 * A create form mirroring the editor is a second place for the same fields to
 * drift apart.
 *
 * Date and time are separate controls because CCDateField is a native
 * `<input type="date">` and the kit has no datetime control. The sidebar splits
 * them the same way, and both compose one ISO instant through `toIso`.
 *
 * The right-hand panel is the one thing here that is not a field: what else is
 * already on while the chosen window runs. Deciding a date is the only part of
 * this form that needs to be checked against the rest of the school, and doing
 * it here saves opening the calendar in another tab to answer it. See
 * EventClashPanel on why it informs rather than blocks.
 */
export const EventCreateDialog: React.FC<EventCreateDialogProps> = ({
	open,
	initialDate,
	initialEnd,
	saving,
	onClose,
	onCreate,
}) => {
	const [title, setTitle] = useState("");
	const [eventType, setEventType] = useState<string | null>(null);
	const { types: eventTypes } = useEventTypes();
	const [allDay, setAllDay] = useState(true);
	const [startDate, setStartDate] = useState("");
	const [startTime, setStartTime] = useState(ALL_DAY_START);
	const [endDate, setEndDate] = useState("");
	const [endTime, setEndTime] = useState(ALL_DAY_END);
	const [error, setError] = useState("");
	// "" is a one-off event, "custom" opens the every-N controls, and the rest
	// are the ready-made choices.
	const [repeat, setRepeat] = useState<RecurrenceFrequency | "custom" | "">("");
	const [customEvery, setCustomEvery] = useState("1");
	const [customUnit, setCustomUnit] = useState<CustomUnit>("weekly");
	// null means "the weekday the event starts on", so it follows the start date
	// until the person picks days themselves.
	const [customDays, setCustomDays] = useState<number[] | null>(null);
	const [endMode, setEndMode] = useState<"count" | "until">("count");
	const [repeatCount, setRepeatCount] = useState("10");
	const [repeatUntil, setRepeatUntil] = useState("");

	// Reset on every open, so an abandoned draft never reappears pre-filled
	// against a different day.
	useEffect(() => {
		if (!open) return;
		const day = toDatePart(initialDate);
		const clickedTime = toTimePart(initialDate);
		// Midnight is what a whole-day cell reports, so it is treated as "no time
		// given" rather than as an event that genuinely starts at 00:00. A
		// midnight start is still reachable — untick All day and set it.
		const timed = !!clickedTime && clickedTime !== ALL_DAY_START;

		setTitle("");
		setEventType(null);
		setAllDay(!timed);
		setStartDate(day);
		setStartTime(timed ? clickedTime : ALL_DAY_START);

		// A drag says where it ended; a click does not, so the click keeps the
		// old assumption of an hour (or the rest of the day when all-day).
		const draggedDay = toDatePart(initialEnd ?? "");
		const draggedTime = toTimePart(initialEnd ?? "");
		if (draggedDay) {
			setEndDate(draggedDay);
			setEndTime(timed ? draggedTime : ALL_DAY_END);
		} else {
			setEndDate(day);
			setEndTime(
				timed
					? addMinutes(clickedTime, DEFAULT_DURATION_MINUTES)
					: ALL_DAY_END
			);
		}
		setError("");
		setRepeat("");
		setCustomEvery("1");
		setCustomUnit("weekly");
		setCustomDays(null);
		setEndMode("count");
		setRepeatCount("10");
		setRepeatUntil("");
	}, [open, initialDate, initialEnd]);

	const start = useMemo(
		() => toIso(startDate, allDay ? ALL_DAY_START : startTime),
		[startDate, startTime, allDay]
	);
	const end = useMemo(
		() => toIso(endDate || startDate, allDay ? ALL_DAY_END : endTime),
		[endDate, startDate, endTime, allDay]
	);

	/** The rule as the API takes it, or undefined for a one-off event. */
	const recurrence = useMemo((): RecurrenceRule | undefined => {
		if (!repeat) return undefined;
		const end =
			endMode === "count"
				? { Count: Number(repeatCount) }
				: { Until: repeatUntil };
		if (repeat !== "custom") return { Frequency: repeat, ...end };
		return {
			Frequency: customUnit,
			Interval: Number(customEvery),
			// Days only mean something when counting in weeks. Left out until
			// the person picks, the API repeats on the start's own weekday.
			...(customUnit === "weekly" && customDays
				? { Weekdays: customDays }
				: {}),
			...end,
		};
	}, [
		repeat,
		customEvery,
		customUnit,
		customDays,
		endMode,
		repeatCount,
		repeatUntil,
	]);

	/** The days shown ticked: the person's own picks, else the start's weekday. */
	const shownDays = customDays ?? (startDate ? [weekdayOf(startDate)] : []);
	const toggleDay = (day: number) => {
		setCustomDays(
			shownDays.includes(day)
				? shownDays.filter((d) => d !== day)
				: [...shownDays, day]
		);
		setError("");
	};

	/** What the rule would create, worked out here so it can be shown first. */
	const expansion = useMemo(() => {
		if (!recurrence) return null;
		if (recurrence.Count !== undefined) {
			const n = recurrence.Count;
			if (!Number.isInteger(n) || n < 2 || n > MAX_OCCURRENCES) {
				return {
					ok: false as const,
					reason: `Enter a number of events from 2 to ${MAX_OCCURRENCES}.`,
				};
			}
		}
		if (recurrence.Until !== undefined && !recurrence.Until) {
			return { ok: false as const, reason: "Choose the date the repeats stop." };
		}
		return expandRecurrence(startDate, recurrence);
	}, [recurrence, startDate]);

	/* The same two instants the payload is built from, so the panel is answering
	   about the event that would actually be created. It does its own debouncing
	   — of the DAYS only, so a time nudge re-filters instantly. */
	const clashes = useConcurrentEvents(start, end, open);

	/** Switching to timed picks a sensible hour rather than leaving 00:00–23:59. */
	const onAllDayChange = (next: boolean) => {
		setAllDay(next);
		/* The "Also on" panel re-narrows on its own — the new times flow into
		   `start`/`end` above and it filters what it holds — but the DAY has not
		   changed, so nothing in its query key moves and it would go on showing
		   an answer fetched up to five minutes ago. This toggle swaps a whole
		   day for an hour of it, which is exactly when that answer is worth
		   being current, and it happens once or twice per dialog rather than per
		   keystroke. See useConcurrentEvents.refresh. */
		clashes.refresh();
		if (next) {
			setStartTime(ALL_DAY_START);
			setEndTime(ALL_DAY_END);
			return;
		}
		const from = `${String(new Date().getHours()).padStart(2, "0")}:00`;
		setStartTime(from);
		setEndTime(addMinutes(from, DEFAULT_DURATION_MINUTES));
	};

	/**
	 * Move the start past the end and the end comes with it.
	 *
	 * The `min` on the Ends field stops an earlier day being PICKED, but it does
	 * nothing about an end that was already valid and got overtaken — that only
	 * surfaced on submit. Same behaviour as the editor sidebar, so setting dates
	 * works the same way in both places.
	 */
	const onStartDate = (value: string) => {
		setStartDate(value);
		// Lexical comparison is safe on `YYYY-MM-DD`.
		if (value && endDate && endDate < value) setEndDate(value);
		setError("");
	};

	const submit = async () => {
		const name = title.trim();
		if (!name) return setError("Give the event a title.");
		if (!startDate) return setError("Choose a start date.");
		if (new Date(end).getTime() <= new Date(start).getTime()) {
			return setError("The end must come after the start.");
		}
		if (expansion && !expansion.ok) return setError(expansion.reason);
		setError("");
		// A failure is surfaced by the caller's toast and the dialog stays open,
		// so nothing typed is lost.
		await onCreate({
			Title: name,
			StartDateTime: start,
			EndDateTime: end,
			Category: NEW_EVENT_CATEGORY,
			/* `|| undefined` rather than the empty string: the field is optional
			   and an absent key is what "no type" means here. */
			EventType: eventType || undefined,
			Recurrence: recurrence,
		});
	};

	return (
		<CCModal
			open={open}
			onClose={onClose}
			title="New event"
			className="em-create-modal"
			footer={
				<>
					<CCButton variant="outline" onClick={onClose} disabled={saving}>
						Cancel
					</CCButton>
					<CCButton
						variant="solid"
						colour="primary"
						onClick={() => void submit()}
						disabled={saving}
					>
						{saving ? "Adding…" : "Add event"}
					</CCButton>
				</>
			}
		>
			<div className="em-create-cols">
				<div className="em-create-form">
					<CCField
						label="Event title"
						type="text"
						fullWidth
						value={title}
						disabled={saving}
						error={error && !title.trim() ? error : undefined}
						onChange={(e) => setTitle(e.target.value)}
					/>

					{/* Asked at creation, not left to the editor, because this is what
					    routes the event to its approvers: an event created without one
					    can be submitted before it has a type at all and go down its
					    calendar's chain instead. Optional all the same, since that
					    calendar fallback is a real answer rather than a failure. */}
					<CCCombobox
						label="Event type"
						fullWidth
						disabled={saving}
						options={eventTypes.map((t) => ({
							value: t.Code,
							label: t.Name ?? t.Code,
						}))}
						value={eventType}
						onChange={(value) => setEventType(value || null)}
					/>

					<div className="em-grid">
						<div className="em-col-md-6">
							<DateTimeRow
								label="Starts"
								date={startDate}
								time={startTime}
								showTime={!allDay}
								disabled={saving}
								onDate={onStartDate}
								onTime={setStartTime}
							/>
						</div>
						<div className="em-col-md-6">
							<DateTimeRow
								label="Ends"
								date={endDate}
								time={endTime}
								showTime={!allDay}
								disabled={saving}
								min={startDate}
								onDate={setEndDate}
								onTime={setEndTime}
							/>
						</div>
					</div>

					<div className="em-create-timing">
						{/* CCSwitch hands over the boolean directly, unlike CCCheckbox
						    which passes the change event. */}
						<CCSwitch
							id="em-create-all-day"
							label="All day"
							checked={allDay}
							disabled={saving}
							onChange={onAllDayChange}
						/>

						{/* Repeats. The first event is the one dated above; the API makes
						    the rest as separate events linked by one series id, so each can
						    still be edited, moved or cancelled on its own afterwards. */}
						<CCSelect
							label="Repeats"
							fullWidth
							disabled={saving}
							placeholder="Does not repeat"
							options={[
								...FREQUENCY_OPTIONS,
								{ value: "custom", label: "Custom..." },
							]}
							value={repeat}
							onChange={(e) => {
								setRepeat(
									e.target.value as RecurrenceFrequency | "custom" | "",
								);
								setError("");
							}}
						/>
					</div>

					{repeat && (
						<div className="em-create-repeat">
							{repeat === "custom" && (
								<>
									{/* "Every [2] [weeks]": the number and the unit sit on one
									    line because they read as one phrase. */}
									<div className="em-create-every">
										<CCField
											label="Repeat every"
											type="number"
											size="sm"
											value={customEvery}
											disabled={saving}
											onChange={(e) => {
												setCustomEvery(e.target.value);
												setError("");
											}}
										/>
										<CCSelect
											label="Unit"
											size="sm"
											options={CUSTOM_UNITS.map((u) => ({
												value: u.value,
												label: u.label(Number(customEvery)),
											}))}
											value={customUnit}
											disabled={saving}
											onChange={(e) => {
												setCustomUnit(e.target.value as CustomUnit);
												setError("");
											}}
										/>
									</div>
									{customUnit === "weekly" && (
										<fieldset className="em-create-categories">
											<legend className="em-label">Repeat on</legend>
											<div className="em-day-chips">
												{WEEKDAY_CHIPS.map((d) => (
													<button
														key={d.value}
														type="button"
														className="em-day-chip"
														aria-pressed={shownDays.includes(d.value)}
														aria-label={d.long}
														title={d.long}
														disabled={saving}
														onClick={() => toggleDay(d.value)}
													>
														{d.short}
													</button>
												))}
											</div>
										</fieldset>
									)}
								</>
							)}
							<fieldset className="em-create-categories">
								<legend className="em-label">Stops</legend>
								<label className="em-create-category">
									<input
										type="radio"
										name="em-create-repeat-end"
										checked={endMode === "count"}
										disabled={saving}
										onChange={() => setEndMode("count")}
									/>
									After a number of events
								</label>
								<label className="em-create-category">
									<input
										type="radio"
										name="em-create-repeat-end"
										checked={endMode === "until"}
										disabled={saving}
										onChange={() => setEndMode("until")}
									/>
									On a date
								</label>
							</fieldset>
							{endMode === "count" ? (
								<CCField
									label="Number of events"
									type="number"
									size="sm"
									fullWidth
									hint="Including the first"
									value={repeatCount}
									disabled={saving}
									onChange={(e) => {
										setRepeatCount(e.target.value);
										setError("");
									}}
								/>
							) : (
								<CCDateField
									label="Last event on or before"
									fullWidth
									value={repeatUntil as never}
									min={startDate as never}
									disabled={saving}
									onChange={(value) => {
										setRepeatUntil(String(value));
										setError("");
									}}
								/>
							)}
							<p className="em-muted" aria-live="polite">
								{expansion?.ok
									? `Creates ${expansion.days.length} events, from ${longDay(
											expansion.days[0]
										)} to ${longDay(
											expansion.days[expansion.days.length - 1]
										)}.`
									: expansion?.reason}
							</p>
						</div>
					)}

					{error && title.trim() && (
						<span className="em-error" role="alert">
							{error}
						</span>
					)}
				</div>

				<EventClashPanel
					events={clashes.events}
					loading={clashes.loading}
					failed={clashes.failed}
					tooWide={clashes.tooWide}
					spanDays={clashes.spanDays}
				/>
			</div>
		</CCModal>
	);
};
